<template>
  <div
    ref="rootEl"
    class="relative min-w-0"
  >
    <button
      type="button"
      :disabled="disabled"
      class="relative h-11 w-full min-w-0 overflow-hidden rounded-md border border-zinc-300 bg-white px-3 pr-9 text-left text-sm normal-case tracking-normal text-zinc-800 transition-colors hover:border-zinc-400 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:border-[#2D8CFF] focus-visible:outline-none"
      :class="open ? 'border-[#2D8CFF]' : ''"
      @click="toggleOpen"
    >
      <span class="flex min-w-0 items-center gap-2 pr-6">
        <img
          v-if="selectedIconSrc"
          :src="selectedIconSrc"
          :alt="selectedIconAlt"
          :class="selectedIconClass"
        >
        <span
          class="min-w-0 truncate"
          :class="selectedOption ? 'text-zinc-800' : 'text-zinc-400'"
        >
          {{ selectedLabel }}
        </span>
        <component
          :is="selectedSuffixIconComponent"
          v-if="selectedSuffixIconComponent"
          :class="selectedSuffixIconClass"
        />
      </span>
      <ChevronDownIcon class="w-4 h-4 text-zinc-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
    </button>

    <div
      v-if="open"
      :class="menuContainerClass"
    >
      <div
        class="app-scroll max-h-56 overflow-auto"
        @scroll="handleMenuScroll"
      >
        <button
          v-if="allowEmpty"
          type="button"
          class="w-full rounded-lg px-3 py-2 text-left text-sm normal-case tracking-normal text-zinc-500 transition hover:bg-zinc-100"
          @click="selectValue('')"
        >
          {{ placeholder }}
        </button>

        <button
          v-for="option in options"
          :key="String(option.value)"
          type="button"
          class="w-full rounded-lg px-3 py-2 text-left text-sm normal-case tracking-normal transition hover:bg-zinc-100"
          :class="isSelected(option.value) ? 'bg-[#2D8CFF] text-white font-semibold shadow-sm' : 'text-zinc-700'"
          @click="selectValue(option.value)"
        >
          <span class="flex items-center justify-between gap-2">
            <span class="flex min-w-0 items-center gap-2">
              <img
                v-if="optionIconSrc(option)"
                :src="optionIconSrc(option)"
                :alt="optionIconAlt(option)"
                :class="optionIconClass(option)"
              >
              <span class="min-w-0 break-words">{{ option.label }}</span>
            </span>
            <component
              :is="optionSuffixIconComponent(option)"
              v-if="optionSuffixIconComponent(option)"
              :class="optionSuffixIconClass(option)"
            />
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { ChevronDownIcon, PlusCircleIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  options: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: 'Select',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  allowEmpty: {
    type: Boolean,
    default: false,
  },
  menuPlacement: {
    type: String,
    default: 'bottom',
  },
});

const emit = defineEmits(['update:modelValue', 'menu-scroll-bottom']);

const open = ref(false);
const rootEl = ref(null);

const selectedOption = computed(() =>
  props.options.find((option) => String(option?.value ?? '') === String(props.modelValue ?? ''))
);

const selectedLabel = computed(() => selectedOption.value?.label || props.placeholder);
const selectedIconSrc = computed(() => optionIconSrc(selectedOption.value));
const selectedIconAlt = computed(() => optionIconAlt(selectedOption.value));
const selectedIconClass = computed(() => optionIconClass(selectedOption.value));
const selectedSuffixIconComponent = computed(() => optionSuffixIconComponent(selectedOption.value));
const selectedSuffixIconClass = computed(() => optionSuffixIconClass(selectedOption.value));
const menuContainerClass = computed(() => {
  const placement = String(props.menuPlacement || '').trim().toLowerCase();
  const base = 'absolute z-[1400] w-full overflow-hidden rounded-xl border border-zinc-200 bg-white p-1 shadow-xl';
  if (placement === 'top') {
    return `${base} bottom-full mb-2`;
  }
  return `${base} mt-2`;
});

function isSelected(value) {
  return String(value ?? '') === String(props.modelValue ?? '');
}

function optionIconSrc(option) {
  if (!option || typeof option !== 'object') return '';
  return String(option.iconSrc || option.icon || '').trim();
}

function optionIconClass(option) {
  if (!option || typeof option !== 'object') return 'h-4 w-4 shrink-0 object-contain';
  const explicit = String(option.iconClass || '').trim();
  return explicit || 'h-4 w-4 shrink-0 object-contain';
}

function optionIconAlt(option) {
  if (!option || typeof option !== 'object') return '';
  const explicit = String(option.iconAlt || '').trim();
  if (explicit) return explicit;
  return String(option.label || 'Option icon').trim();
}

function optionSuffixIconComponent(option) {
  if (!option || typeof option !== 'object') return null;
  const token = String(option.suffixIcon || '').trim().toLowerCase();
  if (token === 'plus-circle' || token === 'plus') return PlusCircleIcon;
  return null;
}

function optionSuffixIconClass(option) {
  if (!option || typeof option !== 'object') return 'h-4 w-4 shrink-0';
  const explicit = String(option.suffixIconClass || '').trim();
  return explicit || 'h-4 w-4 shrink-0';
}

function selectValue(value) {
  emit('update:modelValue', value);
  open.value = false;
}

function toggleOpen() {
  if (props.disabled) return;
  open.value = !open.value;
}

function handleMenuScroll(event) {
  const menuEl = event?.target;
  if (!menuEl) return;
  const remaining = menuEl.scrollHeight - menuEl.scrollTop - menuEl.clientHeight;
  if (remaining <= 24) {
    emit('menu-scroll-bottom');
  }
}

function handleDocumentClick(event) {
  if (!open.value) return;
  if (!rootEl.value) return;
  if (rootEl.value.contains(event.target)) return;
  open.value = false;
}

onMounted(() => {
  document.addEventListener('mousedown', handleDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleDocumentClick);
});
</script>

<style scoped>
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(113, 113, 122, 0.42) transparent;
}

.app-scroll::-webkit-scrollbar {
  width: 7px;
}

.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.app-scroll::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(113, 113, 122, 0.35);
}

.app-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(82, 82, 91, 0.5);
}
</style>
